import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { booksAPI } from '../services/api';
import { decrypt, decryptData } from '../utils/security';

const GRADIENTS = [
  'linear-gradient(150deg,#D4A5A3,#C08A88)',
  'linear-gradient(150deg,#A3B4CC,#8898B8)',
  'linear-gradient(150deg,#A3C4B4,#88AE9E)',
  'linear-gradient(150deg,#C4B488,#AE9E74)',
  'linear-gradient(150deg,#B4A3C8,#9E88B4)',
  'linear-gradient(150deg,#C4A3B4,#AE8E9E)',
];

const BookmarksPage = ({ onOpenBook }) => {
  const { bookmarks, currentUser, toggleBookmark, getToken } = useApp();
  const [books,   setBooks]   = useState({});
  const [loading, setLoading] = useState(true);
  const [toast,   setToast]   = useState(null);

  const bookmarkedIds = (currentUser?.bookmarks || bookmarks || []).map(String);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2000); };

  useEffect(() => {
    if (bookmarkedIds.length === 0) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const results = {};
        await Promise.all(
          bookmarkedIds.map(async (id) => {
            try {
              const res = await booksAPI.getBook(token, id);
              // FIX: decrypt once here instead of on every render.
              if (res.data) results[String(id)] = decryptData(res.data);
            } catch { results[String(id)] = { _id: id, book_name: `Book #${id}` }; }
          })
        );
        setBooks(results);
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [bookmarkedIds.length, getToken]);

  const handleRemove = async (bookId) => {
    await toggleBookmark(String(bookId));
    showToast('Bookmark removed');
  };

  // Last read chapter ID se open karo
const handleOpen = (bookId, book) => {
    const idStr = String(bookId);
    const idNum = parseInt(bookId);
    // String aur number dono try karo
    const pos = currentUser?.bookmarkPos?.[idStr] || currentUser?.bookmarkPos?.[idNum];
    const chapterId = pos?.chapterId || currentUser?.progress?.[idStr]?.chapterId || null;
    const scrollTop = pos?.scrollTop || 0;
    console.log('Opening with:', { chapterId, scrollTop, pos }); // debug
    if (onOpenBook) onOpenBook(book || { _id: bookId }, chapterId, null, scrollTop);
  };
  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'60vh' }}>
      <div style={{ width:28, height:28, border:'2px solid var(--border)', borderTopColor:'var(--rose)', borderRadius:'50%', animation:'spin .8s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (bookmarkedIds.length === 0) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', fontFamily:'Nunito,sans-serif', padding:32 }}>
      <div style={{ fontSize:48, marginBottom:16, opacity:.3 }}>🔖</div>
      <div style={{ fontFamily:'Playfair Display,serif', fontSize:20, fontWeight:700, color:'var(--ink)', marginBottom:8 }}>No bookmarks yet</div>
      <div style={{ fontSize:13, color:'var(--ink-lt)', textAlign:'center', maxWidth:300 }}>
        While reading any book, tap the 🔖 Bookmark button to save it here.
      </div>
    </div>
  );

  return (
    <div style={{ padding:24, fontFamily:'Nunito,sans-serif', maxWidth:900, margin:'0 auto' }}>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontFamily:'Playfair Display,serif', fontSize:26, fontWeight:700, color:'var(--ink)' }}>
          My <em style={{ color:'var(--rose)', fontStyle:'italic' }}>Bookmarks</em>
        </div>
        <div style={{ fontSize:13, color:'var(--ink-lt)', marginTop:4 }}>{bookmarkedIds.length} bookmarked books</div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:16 }}>
        {bookmarkedIds.map((bookId, i) => {
          const book = books[String(bookId)];
          const prog = currentUser?.progress?.[String(bookId)];
          const grad = GRADIENTS[i % GRADIENTS.length];
          const rawName = book?.book_name || book?.book_title || `Book #${bookId}`;
          const title = (() => { try { return decrypt(rawName) || rawName; } catch { return rawName; } })();
          const rawAuthor = book?.authors?.[0]?.name || '';
          const author = (() => { try { return decrypt(rawAuthor) || rawAuthor; } catch { return rawAuthor; } })();
          const rawCat = book?.category?.name || '';
          const category = (() => { try { return decrypt(rawCat) || rawCat; } catch { return rawCat; } })();

          return (
            <div key={bookId} style={{ background:'white', borderRadius:14, border:'1px solid var(--border)', overflow:'hidden', cursor:'pointer', boxShadow:'var(--shadow-xs)', transition:'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow='var(--shadow-md)'; e.currentTarget.style.transform='translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow='var(--shadow-xs)'; e.currentTarget.style.transform='translateY(0)'; }}>
              
              {/* Cover */}
              <div style={{ height:120, background:grad, position:'relative', overflow:'hidden' }}
                onClick={() => handleOpen(bookId, book)}>
                <div style={{ position:'absolute', left:0, top:0, bottom:0, width:4, background:'rgba(255,255,255,.2)' }}/>
                <div style={{ position:'absolute', top:8, right:8, fontSize:14 }}>🔖</div>
                <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'16px 8px 8px', background:'linear-gradient(transparent,rgba(44,44,53,.4))' }}>
                  <div style={{ fontSize:8, fontWeight:600, color:'white', lineHeight:1.3, fontFamily:'Nunito,sans-serif', overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                    {title}
                  </div>
                </div>
                {prog?.percent > 0 && (
                  <div style={{ position:'absolute', bottom:0, left:0, right:0, height:3, background:'rgba(0,0,0,.2)' }}>
                    <div style={{ height:'100%', background:'rgba(255,255,255,.7)', width:`${prog.percent}%` }}/>
                  </div>
                )}
              </div>

              {/* Info */}
              <div style={{ padding:10 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--ink)', marginBottom:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={title}>
                  {title}
                </div>
                {author && <div style={{ fontSize:10, color:'var(--ink-lt)', marginBottom:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{author}</div>}
                {category && <div style={{ fontSize:9, color:'var(--rose)', marginBottom:6, fontWeight:600 }}>{category}</div>}
                {prog?.percent > 0 && (
                  <>
                    <div style={{ height:3, background:'var(--border)', borderRadius:2, marginBottom:6 }}>
                      <div style={{ height:'100%', background:'var(--rose-lt)', borderRadius:2, width:`${prog.percent}%` }}/>
                    </div>
                    <div style={{ fontSize:9, color:'var(--ink-lt)', marginBottom:6 }}>{prog.percent}% read</div>
                  </>
                )}
                <div style={{ display:'flex', gap:6 }}>
                  <button onClick={() => handleOpen(bookId, book)}
                    style={{ flex:1, padding:'5px 0', background:'var(--rose)', color:'white', border:'none', borderRadius:6, fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'Nunito,sans-serif' }}>
                    Continue →
                  </button>
                  <button onClick={() => handleRemove(bookId)}
                    style={{ padding:'5px 8px', background:'var(--rose-pale)', color:'var(--rose)', border:'none', borderRadius:6, fontSize:11, cursor:'pointer' }}>🗑</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {toast && (
        <div style={{ position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)', zIndex:9999, background:'var(--ink)', color:'white', padding:'10px 20px', borderRadius:12, fontSize:12, fontWeight:700, fontFamily:'Nunito,sans-serif', boxShadow:'0 4px 20px rgba(0,0,0,.2)', whiteSpace:'nowrap' }}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default BookmarksPage;